import * as React from 'react';
import axios from '../../utils/axios';
import { fDateSimple } from '../../utils/formatTime';
import { Num, CABECALHO_TABELA_COMPACTO, BotaoExportar } from './index';
import Modal, { classesEntrada } from './Modal';
import { cn } from '../../lib/utils';
import { linhasIniciais, OPCOES_LINHAS, rotuloLinhas } from '../../components/gps/paginacao';

/* ============================================================================
   Historico de logs do dispositivo - visual novo.

   Substitui modal-historico-device.tsx (Dialog do MUI + useTable + TableHeadCustom).
   SOMENTE LEITURA - um GET paginado.

   Contrato, o mesmo de antes:
     GET /dispositivos/paginadoLog?page&rows&valor&campo&direcao&ordenar&deviceId&inicio&fim

   As 11 colunas da tabela antiga estao todas aqui: Device ID, Bateria, Data,
   Endereco, Latitude, Longitude, Wifi, Sinal Telefone, Sinal Dados Moveis,
   Consumo Wifi e Consumo Dados Moveis.

   UM BUG DA VERSAO ANTIGA QUE NAO FOI COPIADO: a URL era montada com template
   string MULTILINHA, entao cada parametro ia com QUEBRA DE LINHA E ESPACOS no
   valor - `&valor=\n        texto`. O `inicio`/`fim` chegavam ao backend como
   "null" com espacos em volta quando nao havia filtro de data. Funcionava por
   tolerancia do driver; aqui a URL e montada em uma linha, e o filtro de data so
   entra quando existe.

   O intervalo de datas voltou de proposito - o modal antigo tinha os dois campos
   (`filterStartDate`/`filterEndDate`) e e o filtro que responde "o que aconteceu
   naquele dia", que e a pergunta de quem abre historico.
   ============================================================================ */

type Log = {
  codigo?: number | string;
  deviceId?: string;
  bateria?: number | string;
  createdAt?: string;
  endereco?: string;
  latitude?: string | number;
  longitude?: string | number;
  wifi?: string;
  sinalTelefone?: string;
  sinal_dados_moveis?: string;
  consumo_wifi?: string;
  consumo_dados_moveis?: string;
};


// Colunas ordenaveis: os nomes vao para `ordenar` na query. Os tres primeiros
// sao os que a tabela antiga usava como padrao/whitelist real.
const COLUNAS: Array<{ campo: keyof Log | string; rotulo: string; largura: string; ordenar?: string; mono?: boolean }> = [
  { campo: 'createdAt', rotulo: 'Data', largura: '150px', ordenar: 'CREATED_AT', mono: true },
  { campo: 'bateria', rotulo: 'Bateria', largura: '90px', ordenar: 'bateria', mono: true },
  { campo: 'endereco', rotulo: 'Endereço', largura: 'minmax(200px,2fr)' },
  { campo: 'latitude', rotulo: 'Latitude', largura: '120px', mono: true },
  { campo: 'longitude', rotulo: 'Longitude', largura: '120px', mono: true },
  { campo: 'wifi', rotulo: 'Wifi', largura: '130px' },
  { campo: 'sinalTelefone', rotulo: 'Sinal telefone', largura: '120px' },
  { campo: 'sinal_dados_moveis', rotulo: 'Sinal dados', largura: '110px' },
  { campo: 'consumo_wifi', rotulo: 'Consumo wifi', largura: '120px', mono: true },
  { campo: 'consumo_dados_moveis', rotulo: 'Consumo dados', largura: '130px', mono: true },
  { campo: 'deviceId', rotulo: 'Device ID', largura: 'minmax(160px,1.4fr)', ordenar: 'DEVICE_ID', mono: true },
];

function texto(v: unknown) {
  const s = v === null || v === undefined || v === '' ? '' : String(v);
  return s || '—';
}

export default function ModalHistoricoDispositivo({
  aberto,
  device,
  onFechar,
}: {
  aberto: boolean;
  device: { deviceId?: string; nome?: string } | null;
  onFechar: () => void;
}) {
  const [linhas, setLinhas] = React.useState<Log[]>([]);
  const [total, setTotal] = React.useState(0);
  const [pagina, setPagina] = React.useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = React.useState(linhasIniciais);
  const [ordenar, setOrdenar] = React.useState('CREATED_AT');
  const [direcao, setDirecao] = React.useState<'asc' | 'desc'>('desc');
  const [inicio, setInicio] = React.useState('');
  const [fim, setFim] = React.useState('');
  const [carregando, setCarregando] = React.useState(false);
  const [erro, setErro] = React.useState('');

  const carregar = React.useCallback(async () => {
    if (!device?.deviceId) return;
    setCarregando(true);
    setErro('');
    try {
      // URL em UMA linha. A versao antiga usava template multilinha e mandava
      // quebra de linha dentro de cada valor.
      let url =
        `/dispositivos/paginadoLog?page=${pagina}&rows=${linhasPorPagina}` +
        `&valor=&campo=deviceId&direcao=${direcao}&ordenar=${ordenar}` +
        `&deviceId=${encodeURIComponent(device.deviceId)}`;

      // Data entra so quando existe - antes ia "null" com espacos em volta.
      if (inicio) url += `&inicio=${inicio}`;
      if (fim) url += `&fim=${fim}`;

      const r = await axios.get<{ content: Log[]; totalElements: number }>(url);
      setLinhas(r.data?.content ?? []);
      setTotal(Number(r.data?.totalElements) || 0);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Ops ocorreu um erro ao carregar a lista');
      setLinhas([]);
    } finally {
      setCarregando(false);
    }
  }, [device?.deviceId, pagina, linhasPorPagina, ordenar, direcao, inicio, fim]);

  React.useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  React.useEffect(() => {
    if (!aberto) return;
    setPagina(0);
    setInicio('');
    setFim('');
  }, [aberto, device?.deviceId]);

  const grade = COLUNAS.map((c) => c.largura).join(' ');
  const totalPaginas = Math.max(1, Math.ceil(total / linhasPorPagina));

  function trocarOrdem(campo?: string) {
    if (!campo) return;
    if (campo === ordenar) setDirecao((v) => (v === 'asc' ? 'desc' : 'asc'));
    else {
      setOrdenar(campo);
      setDirecao('desc');
    }
    setPagina(0);
  }

  return (
    <Modal
      aberto={aberto}
      titulo="Histórico do dispositivo"
      descricao={device ? `${device.nome ?? ''} · ${device.deviceId ?? ''}` : undefined}
      icone="🕘"
      largura="max-w-[1100px]"
      onFechar={onFechar}
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold text-muted-foreground">De</span>
          <input
            type="date"
            value={inicio}
            onChange={(e) => {
              setPagina(0);
              setInicio(e.target.value);
            }}
            className={`${classesEntrada} w-[150px]`}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold text-muted-foreground">Até</span>
          <input
            type="date"
            value={fim}
            onChange={(e) => {
              setPagina(0);
              setFim(e.target.value);
            }}
            className={`${classesEntrada} w-[150px]`}
          />
        </label>

        {(inicio || fim) && (
          <button
            type="button"
            onClick={() => {
              setInicio('');
              setFim('');
              setPagina(0);
            }}
            className="h-9 rounded-lg border border-line px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink"
          >
            Limpar datas
          </button>
        )}

        <span className="ml-auto text-[12px] text-muted-foreground">
          <Num className="font-bold text-ink">{total.toLocaleString('pt-BR')}</Num> registro(s)
        </span>

        {/* EXPORTAR - exporta A PAGINA CARREGADA, e o rotulo diz isso.
            Este historico tem 14 mil linhas num aparelho comum; puxar tudo exigiria
            paginar o backend em looping, e um botao que parece rapido e demora dois
            minutos e pior que um botao honesto. Para levar mais de uma vez, aumente
            "por pagina" (ate 100) e exporte de novo, ou use o filtro de datas.

            As colunas do arquivo saem de `COLUNAS`, a MESMA lista que desenha a
            tabela - assim planilha e tela nunca divergem. */}
        <BotaoExportar
          rotulo={`Excel (${linhas.length})`}
          nomeArquivo={`historico-${device?.deviceId || 'dispositivo'}`}
          linhas={() =>
            linhas.map((l) => {
              const registro: Record<string, any> = {};
              COLUNAS.forEach((c) => {
                registro[c.rotulo] = (l as any)[c.campo] ?? '';
              });
              return registro;
            })
          }
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-line">
        <div className="overflow-x-auto">
          <div className="min-w-[1200px]">
            <div
              className={CABECALHO_TABELA_COMPACTO}
              style={{ gridTemplateColumns: grade }}
            >
              {COLUNAS.map((c) => (
                <div key={String(c.campo)} className="min-w-0 pr-2">
                  {c.ordenar ? (
                    <button
                      type="button"
                      onClick={() => trocarOrdem(c.ordenar)}
                      className={cn(
                        // Cabecalho na cor cheia da paleta: destaque por sublinhado.
                        // `text-ink` aqui era quase preto sobre fundo escuro.
                        'inline-flex max-w-full items-center gap-1 truncate uppercase tracking-[0.07em] hover:underline',
                        ordenar === c.ordenar ? 'underline underline-offset-2' : 'opacity-80 hover:opacity-100'
                      )}
                    >
                      <span className="truncate">{c.rotulo}</span>
                      {ordenar === c.ordenar && <span>{direcao === 'asc' ? '▲' : '▼'}</span>}
                    </button>
                  ) : (
                    // Sem botao quando o backend nao ordena por esta coluna. A
                    // tabela antiga marcava `sort: true` nas 11.
                    <span className="block truncate">{c.rotulo}</span>
                  )}
                </div>
              ))}
            </div>

            <div className="max-h-[48vh] overflow-y-auto">
              {carregando && <div className="p-6 text-center text-[12px] text-muted-foreground">Carregando…</div>}

              {!carregando && erro && <div className="p-6 text-center text-[12px] text-danger">{erro}</div>}

              {!carregando && !erro && !linhas.length && (
                <div className="p-6 text-center text-[12px] text-muted-foreground">
                  Nenhum registro no período.
                </div>
              )}

              {!carregando &&
                !erro &&
                linhas.map((l, i) => (
                  <div
                    key={String(l.codigo ?? i)}
                    className="grid items-center border-b px-3 py-1.5 hover:bg-surface-2"
                    style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)' }}
                  >
                    {COLUNAS.map((c) => {
                      const bruto = (l as any)[c.campo];
                      const valor =
                        c.campo === 'createdAt'
                          ? fDateSimple(l.createdAt) || '—'
                          : c.campo === 'bateria'
                            ? bruto === null || bruto === undefined || bruto === ''
                              ? '—'
                              : `${bruto}%`
                            : texto(bruto);

                      const critico = c.campo === 'bateria' && Number(bruto) <= 20;

                      return (
                        <div key={String(c.campo)} className="min-w-0 pr-2">
                          {c.mono ? (
                            <Num
                              className={cn(
                                'block truncate text-[11px]',
                                critico ? 'font-bold text-danger' : 'text-muted-foreground'
                              )}
                            >
                              {valor}
                            </Num>
                          ) : (
                            <span className="block truncate text-[12px] text-muted-foreground" title={String(valor)}>
                              {valor}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 py-2 text-[12px] text-muted-foreground">
          <span className="flex items-center gap-2">
            <span>
              Mostrando <Num>{linhas.length}</Num> de <Num>{total.toLocaleString('pt-BR')}</Num>
            </span>
            <label className="flex items-center gap-1.5">
              <span className="text-faint">por página</span>
              <select
                value={linhasPorPagina}
                onChange={(e) => {
                  setPagina(0);
                  setLinhasPorPagina(Number(e.target.value));
                }}
                className="h-7 rounded-md border border-line bg-surface px-1.5 text-[12px] tabular-nums outline-none focus:border-primary"
              >
                {OPCOES_LINHAS.map((n) => (
                  <option key={n} value={n}>
                    {rotuloLinhas(n)}
                  </option>
                ))}
              </select>
            </label>
          </span>

          <span className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagina === 0}
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
              className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
            >
              Anterior
            </button>
            <span>
              <Num>{pagina + 1}</Num> / <Num>{totalPaginas}</Num>
            </span>
            <button
              type="button"
              disabled={pagina + 1 >= totalPaginas}
              onClick={() => setPagina((p) => p + 1)}
              className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
            >
              Próxima
            </button>
          </span>
        </div>
      </div>
    </Modal>
  );
}
